import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, MessageSquare, Send, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { sendChatMessage, type ChatMessage } from "@/domain/ai/aiService";
import { getAssistantPageContext, shouldShowAssistant } from "@/domain/ai/assistantContext";
import { useUserLocale } from "@/hooks/useUserLocale";

const buildBudgetContext = async (budgetId: string, t: (key: string) => string) => {
  const { data, error } = await supabase
    .from("budgets")
    .select("status, total_price, projects(name, project_type), budget_versions(version_number)")
    .eq("id", budgetId)
    .single();

  if (error || !data) return "";

  const project = Array.isArray(data.projects) ? data.projects[0] : data.projects;
  const versions = Array.isArray(data.budget_versions) ? data.budget_versions : [];
  const versionNumber = versions.reduce(
    (max, version) => Math.max(max, Number(version.version_number ?? 0)),
    0,
  );

  return [
    `${t("ai.chat.context.budget")}: ${project?.name ?? budgetId}`,
    `${t("review.type")}: ${project?.project_type ?? "-"}`,
    `${t("dashboard.list.version")}: ${versionNumber || 1}`,
    `${t("ai.chat.context.status")}: ${data.status ?? "-"}`,
    `${t("review.totalValue")}: ${data.total_price ?? 0}`,
  ].join("\n");
};

export function OrzioAssistant() {
  const location = useLocation();
  const { t } = useLanguage();
  const { userCountry, userCurrency, userLocale } = useUserLocale();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [budgetContext, setBudgetContext] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus();
    }
  }, [open, messages.length]);

  useEffect(() => {
    let active = true;

    const match = location.pathname.match(/\/budget\/([^/]+)/);
    const budgetId = match?.[1];

    if (!budgetId) {
      setBudgetContext("");
      return;
    }

    const load = async () => {
      const context = await buildBudgetContext(budgetId, t);
      if (active) setBudgetContext(context);
    };

    void load();

    return () => {
      active = false;
    };
  }, [location.pathname, t]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const nextMessages: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const reply = await sendChatMessage(
        supabase,
        nextMessages,
        {
          user_locale: userLocale,
          user_country: userCountry,
          user_currency: userCurrency,
        },
        getAssistantPageContext(location.pathname),
        budgetContext,
      );

      setMessages([...nextMessages, { role: "assistant", content: reply }]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("ai.chat.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  if (!shouldShowAssistant(location.pathname)) {
    return null;
  }

  // Budget editor has a sticky footer bar — lift the button above it so it
  // doesn't obscure the pricing summary on the right side.
  const hasFooterBar = /\/budget\/[^/]+\/edit/.test(location.pathname);
  const buttonBottom = hasFooterBar ? "bottom-[72px]" : "bottom-5";
  const panelBottom = hasFooterBar ? "bottom-[132px]" : "bottom-20";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`fixed ${buttonBottom} right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
        aria-label={t("ai.chat.aria")}
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
      </button>

      {open && (
        <div className={`fixed ${panelBottom} right-5 z-50 flex w-80 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0f1729] shadow-2xl sm:w-96`}>
          <div className="flex items-center gap-2 border-b border-white/8 px-4 py-3">
            <Sparkles className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-semibold text-white/88">{t("ai.chat.title")}</span>
            <span className="ml-auto rounded-full bg-blue-600/20 px-2 py-0.5 text-[10px] font-medium text-blue-300">
              Beta
            </span>
          </div>

          <div
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-[#111b31] px-4 py-3"
            style={{ maxHeight: "320px" }}
          >
            {messages.length === 0 && (
              <p className="py-4 text-center text-xs text-white/40">{t("ai.chat.welcome")}</p>
            )}

            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                  message.role === "user"
                    ? "ml-auto border border-blue-400/30 bg-blue-600 text-white shadow-sm"
                    : "border border-white/10 bg-slate-800/90 text-slate-100 shadow-sm"
                }`}
              >
                <p className="whitespace-pre-wrap break-words selection:bg-blue-500/35 selection:text-white">
                  {message.content}
                </p>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-xs text-white/40">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("ai.chat.thinking")}
              </div>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-white/8 p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("ai.chat.placeholder")}
                rows={1}
                className="min-h-0 flex-1 resize-none rounded-xl border border-blue-400/40 bg-[#18243d] px-3 py-2 text-sm text-slate-50 placeholder:text-slate-400 selection:bg-blue-500/35 selection:text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                style={{
                  maxHeight: "100px",
                  color: "#f8fafc",
                  WebkitTextFillColor: "#f8fafc",
                  caretColor: "#f8fafc",
                }}
              />
              <Button
                size="icon"
                onClick={() => void send()}
                disabled={!input.trim() || loading}
                className="h-9 w-9 flex-shrink-0 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-white/25">{t("ai.chat.hint")}</p>
          </div>
        </div>
      )}
    </>
  );
}
